import { NgModule } from '@angular/core';
import { Router, RouterModule, Routes } from '@angular/router';
import { environment } from 'src/environments/environment';
import { IframeComponent } from './shared/iframe/iframe.component';
import { adminGuard, authGuard } from './guards/route-guard';
import { DashboardComponent } from './components/dashboard/dashboard.component';
import { HomeComponent } from './components/home/home.component';
import { ModulesEnum } from './models/modules';
import { AppAuthResolver } from './resolvers/app-init.resolver';
import { EventsService } from './services/events.service';

function getTitle(moduleName: string) {
  return `Picup | Back Office | ${moduleName}`;
}

const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: ModulesEnum.Dashboard,
    resolve: [AppAuthResolver],
  },
  {
    path: ModulesEnum.Auth,
    component: IframeComponent,
    data: {
      url: environment.childModuleUris[ModulesEnum.Auth],
      route: ModulesEnum.Auth,
    },
  },
  {
    path: ModulesEnum.Dashboard,
    component: DashboardComponent,
    canActivate: [authGuard],
    resolve: [AppAuthResolver],
    title: getTitle('Dashboard'),
    children: [
      {
        path: '',
        component: HomeComponent,
      },
      // Post Dispatch
      {
        path: `${ModulesEnum.PostDispatch}`,
        component: IframeComponent,
        data: {
          url: environment.childModuleUris[ModulesEnum.PostDispatch],
          route: ModulesEnum.PostDispatch,
        },
        title: getTitle('Post Dispatch'),
      },
      {
        path: `${ModulesEnum.Reports}`,
        component: IframeComponent,
        data: {
          url: environment.childModuleUris[ModulesEnum.Reports],
          route: ModulesEnum.Reports,
        },
        title: getTitle('Reports'),
      },
      {
        path: `${ModulesEnum.Reports}/:id**`,
        redirectTo: '/dashboard/reports',
        data: {
          url: environment.childModuleUris[ModulesEnum.Reports],
          route: ModulesEnum.Reports,
        },
      },
      // Driver Management
      {
        path: `${ModulesEnum.DriverManagement}`,
        component: IframeComponent,
        data: {
          url: environment.childModuleUris[ModulesEnum.DriverManagement],
          route: ModulesEnum.DriverManagement,
        },
        title: getTitle('Driver Management'),
      },
      {
        path: `${ModulesEnum.DriverManagement}/:id`,
        component: IframeComponent,
        data: {
          url: environment.childModuleUris[ModulesEnum.DriverManagement],
          route: ModulesEnum.DriverManagement,
        },
      },
      {
        path: `${ModulesEnum.DriverManagement}/:category/:id/:section`,
        component: IframeComponent,
        data: {
          url: environment.childModuleUris[ModulesEnum.DriverManagement],
          route: ModulesEnum.DriverManagement,
        },
      },
      // Zones
      {
        path: `${ModulesEnum.Zones}/:category/:section`,
        component: IframeComponent,
        data: {
          url: environment.childModuleUris[ModulesEnum.Zones],
          route: ModulesEnum.Zones,
        },
        title: getTitle('Zones Bulk Adjustments'),
      },
      {
        path: `${ModulesEnum.Zones}/:id`,
        component: IframeComponent,
        data: {
          url: environment.childModuleUris[ModulesEnum.Zones],
          route: ModulesEnum.Zones,
        },
        title: getTitle('Zones'),
      },
      {
        path: `${ModulesEnum.Zones}/:category/:id`,
        component: IframeComponent,
        data: {
          url: environment.childModuleUris[ModulesEnum.Zones],
          route: ModulesEnum.Zones,
        },
        title: getTitle('Zones'),
      },
      {
        path: `${ModulesEnum.Zones}/:category/:id/:section`,
        component: IframeComponent,
        data: {
          url: environment.childModuleUris[ModulesEnum.Zones],
          route: ModulesEnum.Zones,
        },
        title: getTitle('Zones'),
      },

      {
        path: `${ModulesEnum.Zones}`,
        component: IframeComponent,
        data: {
          url: environment.childModuleUris[ModulesEnum.Zones],
          route: ModulesEnum.Zones,
        },
      },
      // {
      //   path: `${ModulesEnum.CartrackTracker}`,
      //   component: IframeComponent,
      //   data: {
      //     url: environment.childModuleUris[ModulesEnum.CartrackTracker],
      //     route: ModulesEnum.CartrackTracker,
      //   },
      // },
      {
        path: `${ModulesEnum.OrderManagement}`,
        component: IframeComponent,
        data: {
          url: environment.childModuleUris[ModulesEnum.OrderManagement],
          route: ModulesEnum.OrderManagement,
        },
      },
      {
        path: `${ModulesEnum.Banking}`,
        component: IframeComponent,
        data: {
          url: environment.childModuleUris[ModulesEnum.Banking],
          route: ModulesEnum.Banking,
        },
      },
      // Financials
      {
        path: `${ModulesEnum.Financials}`,
        component: IframeComponent,
        data: {
          url: environment.childModuleUris[ModulesEnum.Financials],
          route: ModulesEnum.Financials,
        },
        title: getTitle('Financials'),
      },
      {
        path: `${ModulesEnum.Financials}/:id`,
        component: IframeComponent,
        data: {
          url: environment.childModuleUris[ModulesEnum.Financials],
          route: ModulesEnum.Financials,
        },
      },
      {
        path: `${ModulesEnum.Financials}/:category/:id/:section/:sectionId`, // Adjust this to match your URL structure
        component: IframeComponent,
        data: {
          url: environment.childModuleUris[ModulesEnum.Financials],
          route: ModulesEnum.Financials,
        },
      },
      {
        path: `${ModulesEnum.Financials}/ledgers`,
        component: IframeComponent,
        data: {
          url: environment.childModuleUris[ModulesEnum.Financials],
          route: ModulesEnum.Financials,
        },
      },
      {
        path: `${ModulesEnum.Financials}/ledgers/id**`,
        redirectTo: 'dashboard/ledgers',
        data: {
          url: environment.childModuleUris[ModulesEnum.Financials],
          route: ModulesEnum.Financials,
        },
      },
      {
        path: `${ModulesEnum.Financials}/ledgers/:category/:id`,
        component: IframeComponent,
        data: {
          url: environment.childModuleUris[ModulesEnum.Financials],
          route: ModulesEnum.Financials,
        },
      },
      {
        path: `${ModulesEnum.Admin}`,
        component: IframeComponent,
        canActivate: [adminGuard],
        data: {
          url: environment.childModuleUris[ModulesEnum.Admin],
          route: ModulesEnum.Admin,
        },
      },
      {
        path: `${ModulesEnum.Admin}/:category`,
        component: IframeComponent,
        canActivate: [adminGuard],
        data: {
          url: environment.childModuleUris[ModulesEnum.Admin],
          route: ModulesEnum.Admin,
        },
      },
      {
        path: `${ModulesEnum.Admin}/:category/:id`,
        component: IframeComponent,
        data: {
          url: environment.childModuleUris[ModulesEnum.Admin],
          route: ModulesEnum.Admin,
        },
      },
      {
        path: `${ModulesEnum.Admin}/:category/:section/:id`,
        component: IframeComponent,
        canActivate: [adminGuard],
        data: {
          url: environment.childModuleUris[ModulesEnum.Admin],
          route: ModulesEnum.Admin,
        },
      },
      {
        path: `${ModulesEnum.Admin}/:category/:section/:id`,
        component: IframeComponent,
        data: {
          url: environment.childModuleUris[ModulesEnum.Admin],
          route: ModulesEnum.Admin,
        },
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class AppRoutingModule {
  constructor(private readonly eventService: EventsService, private readonly router: Router) {
    this.router.events.subscribe((event) => {
      if (event.type === 1) {
        this.eventService.childUrl = event.url;
      }
    });
  }
}
